import {
  Component,
  type ErrorInfo,
  type ReactNode,
} from "react";
import { AlertOctagon, RefreshCw } from "lucide-react";

interface Props {
  children: ReactNode;
  /** Optional override for what to render when an error is caught. Receives the
   * caught error + a retry callback that resets the boundary state. */
  fallback?: (error: Error, retry: () => void) => ReactNode;
  /** Short label used in the default fallback heading (e.g. "Programs"). */
  scope?: string;
  /** Side-effect hook for telemetry (Sentry). Called on every catch. */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface State {
  error: Error | null;
}

/**
 * Local error boundary. Wraps a subtree so that a thrown render error
 * doesn't take down the whole shell. Use one at the top level (main.tsx)
 * AND one per major route so a broken chart on Analytics can't kill the
 * sidebar.
 *
 * Why not a hook? React still requires a class for error boundaries.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    if (this.props.onError) {
      try {
        this.props.onError(error, info);
      } catch {
        // Never let telemetry crash the fallback path.
      }
    }
    // Console for dev visibility; replace with Sentry once it's wired.
    // eslint-disable-next-line no-console
    console.error(
      `[ErrorBoundary${this.props.scope ? `:${this.props.scope}` : ""}]`,
      error,
      info,
    );
  }

  retry = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) return this.props.fallback(error, this.retry);

    return <DefaultFallback scope={this.props.scope} error={error} retry={this.retry} />;
  }
}

function DefaultFallback({
  scope,
  error,
  retry,
}: {
  scope?: string;
  error: Error;
  retry: () => void;
}) {
  return (
    <div
      role="alert"
      className="anim-fade-up mx-auto max-w-[480px] my-10 rounded-[var(--radius-card)] surface-base shadow-hairline p-7 text-center"
    >
      <AlertOctagon
        size={28}
        className="mx-auto text-[var(--color-ember)] mb-4"
      />
      <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--color-slate-300)] mb-2">
        {scope ?? "Something"} crashed
      </p>
      <h2 className="text-subheading font-semibold text-[var(--color-snow)] mb-3">
        We caught an error before it took down the page.
      </h2>
      <p className="text-[13px] text-[var(--color-slate-200)] mb-5">
        {error.message || "An unexpected error occurred while rendering."}
      </p>
      <button
        type="button"
        onClick={retry}
        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-[var(--radius-button)] bg-[var(--color-ash-50)] text-[#2f3031] text-[14px] font-medium hover:bg-white transition-colors"
      >
        <RefreshCw size={13} />
        Try again
      </button>
      <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.06em] text-[var(--color-slate-300)]">
        The rest of the platform is still working — try the sidebar.
      </p>
    </div>
  );
}
